import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  Package,
  Layers,
  Building2,
  ShoppingBag,
  Boxes,
  BarChart3,
  Settings,
  LogOut,
  Bell,
  Menu,
  X,
  Award,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL, APP_VERSION } from "@/lib/constants";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/kaliteler", icon: Award, label: "Kalite Yönetimi" },
  { to: "/admin/urunler", icon: Package, label: "Ürün Yönetimi" },
  { to: "/admin/koleksiyonlar", icon: Layers, label: "Koleksiyonlar" },
  { to: "/admin/firmalar", icon: Building2, label: "Firma Yönetimi" },
  { to: "/admin/siparisler", icon: ShoppingBag, label: "Siparişler" },
  { to: "/admin/stok", icon: Boxes, label: "Stok Yönetimi" },
  { to: "/admin/raporlar", icon: BarChart3, label: "Raporlar" },
  { to: "/admin/ayarlar", icon: Settings, label: "Ayarlar" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [notifs, setNotifs] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => { document.title = "Yönetim Paneli – Evser Halı"; }, []);

  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/admin/notifications");
      setNotifs(data);
    } catch {}
  };

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
  }, []);

  // close menu on navigation
  useEffect(() => { setMenuOpen(false); }, [loc.pathname]);
  // body scroll lock
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const unread = notifs.filter((n) => !n.read).length;

  const markAll = async () => {
    await api.put("/admin/notifications/read-all");
    loadNotifs();
  };

  const SidebarBody = ({ prefix = "desktop" }) => (
    <>
      <div className="p-6 border-b border-[#2a2a2a] flex items-center justify-between">
        <Link to="/admin" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="" className="h-10 w-auto bg-white p-1" />
          <div>
            <div className="font-serif text-lg leading-none">Evser Halı</div>
            <div className="text-[0.6rem] tracking-[0.25em] uppercase text-[#c8a97e] mt-1">Admin Panel</div>
          </div>
        </Link>
        <button className="lg:hidden text-[#f7f5f0]/70 hover:text-[#f7f5f0] p-1" onClick={() => setMenuOpen(false)} aria-label="Kapat">
          <X size={20} strokeWidth={1.5} />
        </button>
      </div>
      <nav className="flex-1 py-6 px-3 overflow-y-auto">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            data-testid={`admin-nav-${item.to.split("/").pop() || "dashboard"}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 my-1 text-sm transition-colors ${
                isActive
                  ? "bg-[#c8a97e] text-[#1a1a1a]"
                  : "text-[#d4cfc4] hover:bg-[#1f1f1f] hover:text-[#f7f5f0]"
              }`
            }
          >
            <item.icon size={16} strokeWidth={1.5} />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-[#2a2a2a]">
        <button
          onClick={() => {
            logout();
            nav("/bayi-girisi");
          }}
          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[#d4cfc4] hover:bg-[#1f1f1f]"
          data-testid={`admin-logout-btn-${prefix}`}
        >
          <LogOut size={16} strokeWidth={1.5} /> Çıkış Yap
        </button>
        <div className="text-center text-[0.65rem] uppercase tracking-[0.2em] text-[#6b655c] mt-3" data-testid={`admin-version-${prefix}`}>
          Versiyon {APP_VERSION}
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#f7f5f0]">
      {/* Desktop sidebar (lg+) */}
      <aside
        className="hidden lg:flex w-64 bg-[#121212] text-[#f7f5f0] flex-col fixed inset-y-0 left-0 z-40"
        data-testid="admin-sidebar"
      >
        <SidebarBody prefix="desktop" />
      </aside>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden fixed inset-0 z-50 transition-opacity ${menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        aria-hidden={!menuOpen}
      >
        <div className="absolute inset-0 bg-black/60" onClick={() => setMenuOpen(false)} />
        <aside
          className={`absolute inset-y-0 left-0 w-72 max-w-[85vw] bg-[#121212] text-[#f7f5f0] flex flex-col transition-transform duration-300 ${menuOpen ? "translate-x-0" : "-translate-x-full"}`}
          data-testid="admin-mobile-sidebar"
        >
          <SidebarBody prefix="mobile" />
        </aside>
      </div>

      <div className="lg:ml-64 flex flex-col min-h-screen">
        <header className="bg-white border-b border-[#eae5dc] px-4 md:px-8 h-16 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3 min-w-0">
            <button
              className="lg:hidden p-2 -ml-2 hover:bg-[#f7f5f0]"
              onClick={() => setMenuOpen(true)}
              aria-label="Menüyü aç"
              data-testid="admin-mobile-menu-btn"
            >
              <Menu size={20} strokeWidth={1.5} />
            </button>
            <div className="font-serif text-lg md:text-xl truncate">Yönetim Paneli</div>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <Popover>
              <PopoverTrigger asChild>
                <button className="relative p-2 hover:bg-[#f7f5f0]" data-testid="admin-notifications-btn">
                  <Bell size={18} strokeWidth={1.5} />
                  {unread > 0 && (
                    <span className="absolute top-0 right-0 bg-[#c8a97e] text-[#1a1a1a] text-[0.65rem] rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-medium px-1">
                      {unread}
                    </span>
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-[calc(100vw-2rem)] sm:w-96 p-0 max-w-sm">
                <div className="px-4 py-3 border-b flex items-center justify-between">
                  <span className="font-medium">Bildirimler</span>
                  {unread > 0 && (
                    <Button variant="ghost" size="sm" onClick={markAll} data-testid="admin-notif-mark-all-btn">
                      Tümünü oku
                    </Button>
                  )}
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifs.length === 0 ? (
                    <div className="p-6 text-center text-sm text-[#6b655c]">Bildirim yok</div>
                  ) : (
                    notifs.map((n) => (
                      <Link
                        key={n.id}
                        to={n.link || "#"}
                        className={`block px-4 py-3 border-b text-sm hover:bg-[#f7f5f0] ${!n.read ? "bg-[#fefcf7]" : ""}`}
                      >
                        <div className="font-medium">{n.message}</div>
                        <div className="text-xs text-[#6b655c] mt-1">{new Date(n.created_at).toLocaleString("tr-TR")}</div>
                      </Link>
                    ))
                  )}
                </div>
              </PopoverContent>
            </Popover>
            <div className="hidden sm:block text-right text-sm">
              <div className="font-medium truncate max-w-[160px]">{user?.full_name}</div>
              <div className="text-xs text-[#6b655c] uppercase tracking-wider">Admin</div>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8 overflow-x-hidden" data-testid="admin-main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
